import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useSessionStore } from '@/stores'
import { isPreviewSession } from '@utils/session'
import { useOmpActions } from '../ompActions'
import { usePaymentUpdateAction } from '../hooks/usePaymentUpdateAction'
import { usePortalNavPending } from '../hooks/usePortalNavPending'

const SEEN_KEY = 'omp-payment-update-seen'

// Once per session, not once per page load — a ref resets on refresh, which turned a
// failing payment step into an endless re-open loop.
function alreadyShown(sessionId: number | string): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY) === String(sessionId)
  } catch {
    return false
  }
}

function markShown(sessionId: number | string): void {
  try {
    window.localStorage.setItem(SEEN_KEY, String(sessionId))
  } catch {
    /* private mode — it just re-opens next load */
  }
}

const OmpPaymentUpdateAutoOpen = () => {
  const location = useLocation()
  const { available, start } = usePaymentUpdateAction()
  const { mode, busy, error } = useOmpActions()
  const sessionId = useSessionStore(s => (s.session as any)?.id)
  const preview = useSessionStore(s => isPreviewSession(s.session))
  const resyncing = useSessionStore(s => s.portalResyncing)
  const popupOpen = useSessionStore(s => !!s.popupSession)
  const navPending = usePortalNavPending()
  const fired = useRef(false)

  const onHome = location.pathname === '/portal' || location.pathname === '/portal/'

  useEffect(() => {
    if (
      !onHome ||
      !available ||
      !sessionId ||
      preview ||
      mode !== null ||
      busy ||
      error ||
      resyncing ||
      popupOpen ||
      navPending
    ) {
      return
    }
    if (fired.current || alreadyShown(sessionId)) {
      return
    }
    fired.current = true
    markShown(sessionId)
    start()
  }, [onHome, available, sessionId, preview, mode, busy, error, resyncing, popupOpen, navPending, start])

  return null
}

export default OmpPaymentUpdateAutoOpen
